/*
  COMPONENT: Product Form Runtime

  Owns:
  - Variant select and option-button state
  - Add-to-cart button availability
  - Sold-out availability panel visibility
  - Product price/SKU/inventory display updates
  - Product quantity steppers
  - Product-page sticky buy prompt

  Does not own:
  - Cart mutations
  - Product gallery media navigation
*/

(() => {
  const sellingPlanCache = new WeakMap();

  function strings() {
    return window.CurtisTheme?.strings || {};
  }

  function moneyFromOption(option) {
    return option?.dataset.price || '';
  }

  function controlScope(form) {
    return form.closest('[data-main-product]') || form;
  }

  function sellingPlanMap(form) {
    if (sellingPlanCache.has(form)) return sellingPlanCache.get(form);

    const source = controlScope(form).querySelector('[data-selling-plan-data]');
    let data = {};
    if (source) {
      try {
        data = JSON.parse(source.textContent || '{}');
      } catch (error) {
        data = {};
      }
    }

    sellingPlanCache.set(form, data);
    return data;
  }

  function syncSellingPlanSelect(form, variantOption) {
    const select = controlScope(form).querySelector('[data-selling-plan-select]');
    if (!select) return null;

    const previousValue = select.value;
    const allocations = sellingPlanMap(form)[variantOption?.value || ''] || [];
    const oneTimeOption = select.querySelector('option[value=""]');
    select.replaceChildren(oneTimeOption || new Option(strings().oneTimePurchase || 'One-time purchase', ''));
    const requiresSellingPlan = variantOption?.dataset.requiresSellingPlan === 'true';
    const oneTimePurchase = select.querySelector('option[value=""]');
    if (oneTimePurchase) oneTimePurchase.disabled = requiresSellingPlan;

    allocations.forEach((allocation) => {
      select.add(new Option(allocation.name, allocation.id));
    });

    if (allocations.some((allocation) => allocation.id === previousValue)) {
      select.value = previousValue;
    } else {
      select.value = requiresSellingPlan ? allocations[0]?.id || '' : '';
    }
    select.closest('[data-selling-plans]')?.toggleAttribute('hidden', allocations.length === 0);
    return allocations.find((allocation) => allocation.id === select.value) || null;
  }

  function selectedOptionValues(form) {
    return Array.from(controlScope(form).querySelectorAll('[data-product-option-input]:checked')).map((input) => input.value);
  }

  function syncSelectFromOptionButtons(form) {
    const select = controlScope(form).querySelector('[data-variant-select]');
    const values = selectedOptionValues(form);
    if (!select || !values.length) return;

    const match = Array.from(select.options).find((option) => {
      const optionValues = (option.dataset.optionValues || '').split('||');
      return optionValues.length === values.length && optionValues.every((value, index) => value === values[index]);
    });

    if (match) {
      select.value = match.value;
    } else {
      select.selectedIndex = -1;
    }
  }

  function inventoryText(option, statusElement) {
    const themeStrings = strings();
    const available = option?.dataset.available === 'true';
    const inventoryManagement = option?.dataset.inventoryManagement || '';
    const inventoryPolicy = option?.dataset.inventoryPolicy || '';
    const inventoryQuantity = Number(option?.dataset.inventoryQuantity || 0);
    const inventoryLow = option?.dataset.inventoryLow === 'true';
    const lowStockThreshold = Number(statusElement?.dataset.lowStockThreshold || 5);
    const showQuantity = statusElement?.dataset.showInventoryQuantity === 'true';

    if (!available) return themeStrings.soldOut || 'Sold out';

    if (inventoryManagement && inventoryLow) {
      if (showQuantity && themeStrings.lowStockWithCount) {
        return themeStrings.lowStockWithCount.replace('__count__', inventoryQuantity);
      }
      return themeStrings.lowStock || 'Low stock';
    }

    if (inventoryManagement && inventoryQuantity <= 0 && inventoryPolicy === 'continue') {
      return themeStrings.availableToOrder || 'Available to order';
    }

    return themeStrings.inStock || 'In stock';
  }

  function updateForm(form) {
    const root = form.closest('[data-main-product]');
    const scope = root || form;
    const select = scope.querySelector('[data-variant-select]');
    const button = form.querySelector('[data-add-to-cart]');
    const idInput = form.querySelector('[data-product-variant-id]');
    if ((!select && !idInput) || !button) return;

    const option = select ? select.options[select.selectedIndex] : idInput;
    const hasVariant = Boolean(option?.value);
    const variantAvailable = hasVariant && option.dataset.available === 'true';
    const requiresSellingPlan = option?.dataset.requiresSellingPlan === 'true';
    const sellingPlanAllocation = syncSellingPlanSelect(form, option);
    const available = variantAvailable && (!requiresSellingPlan || Boolean(sellingPlanAllocation));
    const basePrice = sellingPlanAllocation?.price || moneyFromOption(option);
    const anniversaryActive = option?.dataset.anniversaryActive === 'true';
    const anniversaryPrice = sellingPlanAllocation?.anniversaryPrice || option?.dataset.anniversaryPrice || '';
    const anniversaryReward = option?.dataset.anniversaryReward || '';
    const price = anniversaryActive && anniversaryPrice ? anniversaryPrice : basePrice;
    const compareAtPrice = anniversaryActive
      ? ''
      : sellingPlanAllocation
        ? sellingPlanAllocation.compareAtPrice
        : option?.dataset.compareAtPrice || '';
    const unitPrice = sellingPlanAllocation ? sellingPlanAllocation.unitPrice : option?.dataset.unitPrice || '';
    const unitReferenceValue = option?.dataset.unitReferenceValue || '';
    const unitReferenceUnit = option?.dataset.unitReferenceUnit || '';
    const sku = option?.dataset.sku || '';
    const mediaId = option?.dataset.featuredMediaId || '';

    if (idInput) idInput.value = option?.value || '';
    root?.querySelectorAll('[data-payment-terms-variant]').forEach((input) => {
      input.value = option?.value || '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    button.disabled = !available;
    const buttonLabel = button.querySelector('[data-add-to-cart-label]');
    const buttonText = available
      ? strings().addToCart || button.dataset.addText
      : hasVariant && !variantAvailable
        ? strings().soldOut || button.dataset.soldOutText
        : strings().unavailable || button.dataset.unavailableText;
    if (buttonLabel) {
      buttonLabel.textContent = buttonText;
    } else {
      button.textContent = buttonText;
    }

    const currentPrice = root?.querySelector('[data-price-current]');
    const beforePrice = root?.querySelector('[data-price-before]');
    const rewardPrice = root?.querySelector('[data-price-reward-value]');
    const comparePrice = root?.querySelector('[data-price-compare]');
    const saleLabel = root?.querySelector('[data-price-sale-label]');
    const regularLabel = root?.querySelector('[data-price-regular-label]');
    const soldOutPrice = root?.querySelector('[data-price-sold-out]');
    const unitPriceElement = root?.querySelector('[data-price-unit]');
    const unitPriceValue = root?.querySelector('[data-price-unit-value]');
    const unitReferenceValueElement = root?.querySelector('[data-price-unit-reference-value]');
    const unitReferenceUnitElement = root?.querySelector('[data-price-unit-reference-unit]');
    const skuElement = root?.querySelector('[data-product-sku]');
    const stickyPrice = root?.querySelector('[data-sticky-price]');
    const stickyRewardLabel = root?.querySelector('[data-sticky-reward-label]');
    const stickyButton = root?.querySelector('[data-sticky-add-to-cart]');
    const stickyBuyNow = root?.querySelector('[data-sticky-buy-now]');
    const inventoryStatus = root?.querySelector('[data-inventory-status]');
    const soldOutPanel = root?.querySelector('[data-sold-out-panel]');
    const dynamicCheckout = form.querySelector('[data-product-dynamic-checkout]');

    if (currentPrice) currentPrice.textContent = price;
    if (beforePrice) beforePrice.textContent = basePrice;
    if (rewardPrice) rewardPrice.textContent = anniversaryReward;
    if (stickyPrice) stickyPrice.textContent = price;
    if (stickyRewardLabel) stickyRewardLabel.hidden = !anniversaryActive;
    if (stickyButton) {
      stickyButton.disabled = !available;
      stickyButton.textContent = available
        ? strings().addToCart || stickyButton.dataset.addText
        : hasVariant && !variantAvailable
          ? strings().soldOut || stickyButton.dataset.soldOutText
          : strings().unavailable || button.dataset.unavailableText;
    }
    if (stickyBuyNow) stickyBuyNow.disabled = !available;
    if (comparePrice) {
      comparePrice.textContent = compareAtPrice;
      comparePrice.hidden = !compareAtPrice;
    }
    if (saleLabel) saleLabel.hidden = !compareAtPrice;
    if (regularLabel) regularLabel.hidden = !compareAtPrice;
    if (soldOutPrice) soldOutPrice.hidden = !hasVariant || variantAvailable;
    if (unitPriceElement) unitPriceElement.hidden = !hasVariant || !unitPrice || !unitReferenceUnit;
    if (unitPriceValue) unitPriceValue.textContent = unitPrice;
    if (unitReferenceValueElement) unitReferenceValueElement.textContent = unitReferenceValue;
    if (unitReferenceUnitElement) unitReferenceUnitElement.textContent = unitReferenceUnit;
    if (skuElement) {
      skuElement.textContent = sku;
      skuElement.closest('[data-product-sku-row]')?.toggleAttribute('hidden', !sku);
    }
    if (inventoryStatus) {
      inventoryStatus.textContent = inventoryText(option, inventoryStatus);
      inventoryStatus.hidden = !hasVariant || !variantAvailable;
    }
    if (soldOutPanel) {
      soldOutPanel.hidden = !hasVariant || variantAvailable;
    }
    if (dynamicCheckout) dynamicCheckout.hidden = !available;

    if (root) {
      root.dispatchEvent(
        new CustomEvent('product:variant-change', {
          bubbles: true,
          detail: {
            available,
            mediaId,
            price,
            sellingPlanId: sellingPlanAllocation?.id || '',
            variantId: option?.value,
          },
        })
      );
    }
  }

  function initializeProductForms(root = document) {
    root.querySelectorAll('[data-product-form]').forEach((form) => {
      if (form.dataset.initialized === 'true') return;
      form.dataset.initialized = 'true';

      updateForm(form);

      const scope = controlScope(form);
      scope.addEventListener('change', (event) => {
        if (event.target.matches('[data-product-option-input]')) {
          syncSelectFromOptionButtons(form);
          updateForm(form);
        }

        if (event.target.matches('[data-variant-select]')) updateForm(form);
        if (event.target.matches('[data-selling-plan-select]')) updateForm(form);
      });

      scope.querySelectorAll('[data-product-quantity-step]').forEach((button) => {
        button.addEventListener('click', () => {
          const input = scope.querySelector('[data-product-quantity]');
          if (!input) return;
          input.value = Math.max(Number(input.min || 1), Number(input.value || 1) + Number(button.dataset.productQuantityStep || 0));
        });
      });
    });

    root.querySelectorAll('[data-scroll-to-product-form]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';

      button.addEventListener('click', () => {
        const form = document.querySelector('[data-main-product] [data-product-form]');
        if (!form) return;
        form.scrollIntoView({ behavior: 'smooth', block: 'center' });
        const target = form.querySelector('[data-add-to-cart], [data-variant-select], [data-product-option-input]');
        if (target) window.setTimeout(() => target.focus(), 350);
      });
    });

    root.querySelectorAll('[data-sticky-buy-now]').forEach((button) => {
      if (button.dataset.initialized === 'true') return;
      button.dataset.initialized = 'true';

      button.addEventListener('click', () => {
        if (button.disabled) return;
        const productRoot = button.closest('[data-main-product]');
        const form = productRoot?.querySelector('[data-product-form]');
        const paymentButton = form?.querySelector(
          '[data-product-dynamic-checkout]:not([hidden]) .shopify-payment-button__button:not([disabled]), [data-product-dynamic-checkout]:not([hidden]) .shopify-payment-button button:not([disabled])'
        );
        if (!paymentButton) return;
        paymentButton.click();
      });
    });

    root.querySelectorAll('[data-sticky-submit-to-cart]').forEach((button) => {
      if (button.dataset.submitInitialized === 'true') return;
      button.dataset.submitInitialized = 'true';

      button.addEventListener('click', () => {
        if (button.disabled) return;
        const productRoot = button.closest('[data-main-product]');
        const form = productRoot?.querySelector('[data-product-form]');
        const addButton = form?.querySelector('[data-add-to-cart]');
        if (!form || !addButton || addButton.disabled) return;

        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit(addButton);
        } else {
          addButton.click();
        }
      });
    });

    root.querySelectorAll('[data-main-product]').forEach((productRoot) => {
      const stickyBar = productRoot.querySelector('[data-product-sticky-bar]');
      const buttons = productRoot.querySelector('.main-product__buttons');
      if (!stickyBar || !buttons || stickyBar.dataset.visibilityInitialized === 'true') return;
      stickyBar.dataset.visibilityInitialized = 'true';

      const updateStickyVisibility = (visible) => {
        stickyBar.classList.toggle('is-visible', visible);
        stickyBar.toggleAttribute('inert', !visible);
        stickyBar.setAttribute('aria-hidden', visible ? 'false' : 'true');
        document.body.classList.toggle('product-sticky-visible', visible);
      };

      const setStickyVisibility = () => {
        const rect = buttons.getBoundingClientRect();
        updateStickyVisibility(rect.bottom < 0);
      };

      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            const entry = entries[0];
            updateStickyVisibility(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
          },
          { threshold: 0 }
        );
        observer.observe(buttons);
        window.CurtisTheme?.registerCleanup?.(productRoot, () => {
          observer.disconnect();
          document.body.classList.remove('product-sticky-visible');
        });
      } else {
        setStickyVisibility();
        window.addEventListener('scroll', setStickyVisibility, { passive: true });
        window.addEventListener('resize', setStickyVisibility);
        window.CurtisTheme?.registerCleanup?.(productRoot, () => {
          window.removeEventListener('scroll', setStickyVisibility);
          window.removeEventListener('resize', setStickyVisibility);
          document.body.classList.remove('product-sticky-visible');
        });
      }
    });
  }

  initializeProductForms();
  document.addEventListener('shopify:section:load', (event) => initializeProductForms(event.target));
})();
